<script setup>
import {ref, computed, toRef, watch} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import ColorPicker from 'primevue/colorpicker';
import Button from "primevue/button";
import EmailTemplatePreview from "./EmailTemplatePreview.vue";
import FileUploader from "./FileUploader.vue";
import IconField from "primevue/iconfield";
import InputMask from "primevue/inputmask";
import InputIcon from "primevue/inputicon";
import {useForm} from "laravel-precognition-vue-inertia";
import {usePage} from "@inertiajs/vue3";

const props = defineProps({
    text: String,
    template: Object
})

const visible = defineModel();

const form = useForm('post', '/app/email-themes/process', {
    id: null,
    name: '',
    accent_color: '',
    background_color: '',
    text_color: '',
    background_image: '',
    logo_image: '',
    footer: '',
    font: '',
    font_url: ''
});

watch(() => props.template, (val) => {
    form.id = val?.id ?? null;
    form.name = val?.name ?? '';
    form.accent_color = val?.accent_color ?? '0033ff';
    form.background_color = val?.background_color ?? 'f9F9F9';
    form.text_color = val?.text_color ?? '000000';
    form.background_image = val?.background_image ?? null;
    form.logo_image = val?.logo_image ?? `${usePage().props.olympus.files_url}/logo.svg`;
    form.footer = val?.footer ?? '';
    form.font = val?.font ?? 'Syne';
    form.font_url = val?.font_url ?? 'https://fonts.googleapis.com/css2?family=Syne:wght@400..800&display=swap';
}, {immediate: true});

const wrappedText = computed(() => {
    return props.text.replace(/%([^%]+)%/g, '<span class="bg-blue-100 rounded-lg p-1 py-0.5">%$1</span>');
});

const submit = () => {
    form.submit({
        onSuccess: () => {
            visible.value = false;
        }
    })
}
</script>

<template>
    <Dialog v-model:visible="visible" modal :header="$t('forms.email_creator_title')">
        <div class="flex">
            <div class="border dark:border-neutral-700 border-r-0 rounded-r-none rounded-lg flex flex-col">
                <div class="flex flex-col gap-2 items-center justify-center border-b dark:border-gray-800 p-4 pt-3">
                    <label class="text-sm">
                        {{ $t('forms.email_creator_template_name') }}
                    </label>
                    <InputText v-model="form.name" :invalid="form.invalid('name')" @change="form.validate('name')" style="width: 170px"></InputText>
                    <small v-if="form.invalid('name')" class="text-red-500">{{form.errors['name']}}</small>
                </div>
                <div class="flex flex-col gap-2 items-center justify-center border-b dark:border-gray-800 p-4 pt-3">
                    <label class="text-sm">
                        {{ $t('forms.email_creator_logo') }}
                    </label>
                    <file-uploader v-model:src="form.logo_image"></file-uploader>
                </div>
                <div class="flex flex-col gap-2 items-center justify-center border-b dark:border-gray-800 p-4 pt-3">
                    <label class="text-sm">
                        {{ $t('forms.email_creator_bg') }}
                    </label>
                    <div class="flex gap-2 items-center">
                        <div class="border rounded-lg overflow-hidden">
                            <ColorPicker v-model="form.background_color" @change="form.validate('background_color')" format="hex"></ColorPicker>
                        </div>
                        <div style="width: 150px">
                            <IconField>
                                <InputIcon class="pi pi-hashtag"></InputIcon>
                                <InputMask v-model="form.background_color" @change="form.validate('background_color')" :invalid="form.invalid('background_color')" mask="******" fluid
                                           class="w-full"></InputMask>
                            </IconField>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col gap-2 items-center justify-center border-b dark:border-gray-800 p-4 pt-3">
                    <label class="text-sm">
                        {{ $t('forms.email_creator_text_color') }}
                    </label>
                    <div class="flex gap-2 items-center">
                        <div class="border rounded-lg overflow-hidden">
                            <ColorPicker v-model="form.text_color" @change="form.validate('text_color')" format="hex"></ColorPicker>
                        </div>
                        <div style="width: 150px">
                            <IconField>
                                <InputIcon class="pi pi-hashtag"></InputIcon>
                                <InputMask v-model="form.text_color" @change="form.validate('text_color')" :invalid="form.invalid('text_color')" mask="******" fluid
                                           class="w-full"></InputMask>
                            </IconField>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col gap-2 items-center justify-center border-b dark:border-gray-800 p-4 pt-3">
                    <label class="text-sm">
                        {{ $t('forms.email_creator_footer') }}
                    </label>
                    <InputText v-model="form.footer" @change="form.validate('footer')" :invalid="form.invalid('footer')" style="width: 170px"></InputText>
                </div>
                <div class="flex flex-col gap-2 items-center justify-center dark:border-gray-800 p-3 ">
                    <Button :label="$t('common.save')" :loading="form.processing" @click="submit" class="w-full"></Button>
                </div>
            </div>
            <email-template-preview :template="form" class="rounded-l-none">
                <template #text-1>
                    <div class="content" v-html="wrappedText"></div>
                </template>
            </email-template-preview>
        </div>
    </Dialog>
</template>

<style scoped lang="scss">

</style>
